import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthService, GlobalMeasureChecksService, QueryService, WorkspaceService } from '@pt/api-client';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  DocRenameRequest,
  DocumentDuplicateResponse,
  WorkspaceItem,
  WorkspaceItemType,
  WorkspaceMoveRequest
} from '@primatoo-ms-organization';
import { of } from 'rxjs';
import { WidgetDialogService } from '../../services/widget-dialog.service';
import * as RiskAssessmentActions from '../actions/risk-assessment.actions';

import { PtCoreActions } from '@pt/pt-core';
import { DialogService } from '../../../services';
import { TranslateService } from '@ngx-translate/core';
import { ValidateDocumentRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/ValidateDocumentRequest';
import { CreateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/CreateDocResponse';
import {
  CreateGlobalMeasureDocRequest
} from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheckdoc/CreateGlobalMeasureDocRequest';
import { UpdateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpdateDocResponse';
import {
  UpdateGlobalMeasureDocRequest
} from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheckdoc/UpdateGlobalMeasureDocRequest';

@Injectable()
export class RiskAssessmentEffects {

  publishRiskAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiskAssessmentActions.publishRiskAssessment),
      switchMap((action: { docId: number, skipBack?: boolean }) =>
        this.riskAssessmentService.publish(action.docId).pipe(
          switchMap(() => {
            if (action?.skipBack) {
              return [
                RiskAssessmentActions.publishRiskAssessmentSuccess(),
                PtCoreActions.showOperationSuccessNotification()
              ];
            } else {
              return [
                RiskAssessmentActions.publishRiskAssessmentSuccess(),
                PtCoreActions.navigateBack({
                  hop: -1,
                  queryParams: {
                    opened: true,
                    id: action.docId
                  }
                }),
                PtCoreActions.showOperationSuccessNotification()
              ];
            }
          }),
          catchError(err =>
            of(RiskAssessmentActions.publishRiskAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  publishRiskAssessmentWithConfirm$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(RiskAssessmentActions.publishRiskAssessmentWithConfirm),
        map((action: { docId: number, skipBack?: boolean, hideInfo?: boolean }) => {
          const title = 'TITLE.RISK_ASSESSMENT_PUBLISH';
          const message = 'MESSAGE.RISK_ASSESSMENT_PUBLISH';
          // const info = 'MESSAGE.ON_END_OPERATION_COME_BACK';
          return this.dialogService.runValidateRequestDialog(title, message
            // !action.hideInfo ? info : null
          ).subscribe((response) => {
              if (response) {
                this.store.dispatch(RiskAssessmentActions.publishRiskAssessment({
                  docId: action?.docId,
                  skipBack: action?.skipBack
                }));
              }
            });
        })
      );
    }, { dispatch: false }
  );

  validateDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiskAssessmentActions.validateRiskAssessment),
      switchMap((action: { docId: number, request: ValidateDocumentRequest, skipBack?: boolean }) =>
        this.riskAssessmentService.validate(action.docId, action.request).pipe(
          switchMap(() => {
            if (action?.skipBack) {
              return [
                RiskAssessmentActions.validateRiskAssessmentSuccess(),
                PtCoreActions.showOperationSuccessNotification()
              ];
            } else {
              return [
                RiskAssessmentActions.validateRiskAssessmentSuccess(),
                PtCoreActions.navigateBack({
                  hop: -1,
                  queryParams: {
                    opened: true,
                    id: action.docId
                  }
                }),
                PtCoreActions.showOperationSuccessNotification()
              ];
            }
          }),
          catchError(err =>
            of(RiskAssessmentActions.validateRiskAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });

  validateDocumentWithConfirm$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(RiskAssessmentActions.validateRiskAssessmentWithConfirm),
        map((action: { docId: number, skipBack?: boolean }) => {
          const title = 'TITLE.RISK_ASSESSMENT_VALIDATE';
          const message = 'MESSAGE.RISK_ASSESSMENT_VALIDATE';
          return this.dialogService.runValidateRequestDialog(
            title,
            message
          ).subscribe((request) => {
              if (request) {
                // this.store.dispatch(PtCoreActions.showOperationRunning());
                this.store.dispatch(RiskAssessmentActions.validateRiskAssessment({
                  docId: action.docId,
                  request,
                  skipBack: action.skipBack
                }));
              } else {
                this.store.dispatch(RiskAssessmentActions.validateRiskAssessmentFailed({ error: null }));
              }
            });
        })
      );
    }, { dispatch: false }
  );

  reopenRiskAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiskAssessmentActions.reopenRiskAssessment),
      switchMap((action: { id: number }) =>
        this.riskAssessmentService.reopen(action.id).pipe(
          switchMap(() =>
            [
              PtCoreActions.showOperationSuccessNotification(),
              RiskAssessmentActions.reopenRiskAssessmentSuccess({ id: action.id })
            ]
          ),
          catchError(err =>
            of(RiskAssessmentActions.reopenRiskAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  reopenRiskAssessmentWithConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskAssessmentActions.reopenRiskAssessmentWithConfirm),
      switchMap((action) => {
        const title = 'TITLE.RISK_ASSESSMENT_REOPEN';
        const message = 'MESSAGE.RISK_ASSESSMENT_REOPEN';
        return this.widgetDialogService.runConfirmDialog(title, message).pipe(
          map((response) => {
            if (response) {
              this.store.dispatch(RiskAssessmentActions.reopenRiskAssessment({
                id: action?.id
              }));
            }
          })
        );
      })
    ), { dispatch: false }
  );
  updateRiskAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiskAssessmentActions.updateRiskAssessment),
      switchMap((action: { id: number, request: UpdateGlobalMeasureDocRequest }) =>
        this.riskAssessmentService.update(action.id, action.request).pipe(
          map((response: UpdateDocResponse) =>
            RiskAssessmentActions.updateRiskAssessmentSuccess({ response })
          ),
          catchError(err =>
            of(RiskAssessmentActions.updateRiskAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  renameRiskAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiskAssessmentActions.renameRiskAssessment),
      switchMap((action: { id: number, request: DocRenameRequest }) =>
        this.riskAssessmentService.rename(action.id, action.request).pipe(
          map(() =>
            RiskAssessmentActions.renameRiskAssessmentSuccess({ id: action.id })
          ),
          catchError(err =>
            of(RiskAssessmentActions.renameRiskAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  createRiskAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiskAssessmentActions.createRiskAssessment),
      switchMap((action: { request: CreateGlobalMeasureDocRequest }) =>
        this.riskAssessmentService.create(action.request).pipe(
          map((response: CreateDocResponse) =>
            RiskAssessmentActions.createRiskAssessmentSuccess({ id: response.docId })
          ),
          catchError(err =>
            of(RiskAssessmentActions.createRiskAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  moveRiskAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiskAssessmentActions.moveRiskAssessment),
      switchMap((action: { id: number, workspaceMoveRequest: WorkspaceMoveRequest }) =>
        this.workspaceService.move(action.id, WorkspaceItemType.GLOBAL_MEASURE_CHECK, action.workspaceMoveRequest).pipe(
          map((item: WorkspaceItem) =>
            RiskAssessmentActions.moveRiskAssessmentSuccess({ item })
          ),
          catchError(err => of(
            RiskAssessmentActions.moveRiskAssessmentFailed({ error: err.message })
          ))
        )
      )
    );
  });

  duplicateRiskAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiskAssessmentActions.duplicateRiskAssessment),
      switchMap((action: { id: number, request: CreateGlobalMeasureDocRequest }) =>
        this.riskAssessmentService.duplicate(action.id, action.request).pipe(
          map((response: DocumentDuplicateResponse) =>
            RiskAssessmentActions.duplicateRiskAssessmentSuccess({ response })
          ),
          catchError(err => of(
            RiskAssessmentActions.duplicateRiskAssessmentFailed({
              error: err?.message
            })
          ))
        )
      )
    );
  });
  deleteRiskAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RiskAssessmentActions.deleteRiskAssessment),
      switchMap((action: { id: number }) =>
        this.riskAssessmentService.delete(action.id).pipe(
          map(() => RiskAssessmentActions.deleteRiskAssessmentSuccess()),
          catchError(err => of(
            RiskAssessmentActions.deleteRiskAssessmentFailed({
              error: err.message
            })
          ))
        )
      ));
  });

  constructor(
    private store: Store,
    private actions$: Actions,
    private authService: AuthService,
    private queryService: QueryService,
    private widgetDialogService: WidgetDialogService,
    private riskAssessmentService: GlobalMeasureChecksService,
    private workspaceService: WorkspaceService,
    private dialogService: DialogService,
    private translateService: TranslateService
  ) {
  }

  // deleteRiskAssessmentWithVersion$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(RiskAssessmentActions.deleteRiskAssessmentWithVersion),
  //     switchMap((action: { id: number, version: number }) => {
  //         const title = this.translateService.stream('TITLE.RISK_ASSESSMENT_DELETE');
  //         const message = this.translateService.stream('MESSAGE.RISK_ASSESSMENT_DELETE');
  //         return this.dialogService.runConfirmDialogWithValidation({
  //           title,
  //           message,
  //           dialogType: DialogType.DELETE,
  //           size: 'md'
  //         }).pipe(
  //           map((response) => {
  //             if (response) {
  //               this.riskAssessmentService.deleteByVersion(action.id, action.version).subscribe(
  //                 (success) => {
  //                   this.store.dispatch(RiskAssessmentActions.deleteRiskAssessmentWithVersionSuccess());
  //                   this.store.dispatch(PtCoreActions.navigateBack({
  //                     hop: -1,
  //                     queryParams: {
  //                       opened: true,
  //                       id: action.id
  //                     }
  //                   }));
  //                   this.store.dispatch(PtCoreActions.showOperationSuccessNotification());
  //                 },
  //                 (error) => RiskAssessmentActions.deleteRiskAssessmentWithVersionFailed({
  //                   error: error.message
  //                 })
  //               )
  //             }
  //           })
  //         );
  //       }
  //     )
  //   ), {dispatch: false}
  // );

}
